import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { api } from "../../../api";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";
import { FileText, Plus, Clock, CheckCircle, XCircle, Ban, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

interface Contract {
  id: number;
  title: string;
  status: 'pending' | 'signed' | 'rejected' | 'canceled' | 'expired';
  file_url: string | null;
  signing_url?: string;
  created_at: string;
}

interface ContractsSectionProps {
  clientId: number;
  clientName: string;
}

export function ContractsSection({ clientId, clientName }: ContractsSectionProps) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].contractsSection;
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const res = await api.listClientContracts(clientId);
    if (res.ok) setContracts(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (clientId) load();
  }, [clientId]);

  const getStatusBadge = (status: Contract['status']) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-600 border border-amber-100/50">
            <Clock className="h-3 w-3" />
            {t.status.pending}
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500 border border-neutral-200">
            <Clock className="h-3 w-3 opacity-50" />
            {lang === 'pt' ? 'Expirado' : 'Expired'}
          </span>
        );
      case 'signed':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600 border border-emerald-100/50">
            <CheckCircle className="h-3 w-3" />
            {t.status.signed}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-red-600 border border-red-100/50">
            <XCircle className="h-3 w-3" />
            {t.status.rejected}
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500 border border-neutral-100">
            <Ban className="h-3 w-3" />
            {t.status.canceled}
          </span>
        );
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden flex flex-col">
      <div className="p-6 border-b border-neutral-50 flex items-center justify-between bg-gradient-to-r from-white to-neutral-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <span className="font-bold text-neutral-800 tracking-tight">{t.title}</span>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-4">
            <div className="h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-300">
              {lang === 'pt' ? "Carregando documentos..." : "Loading documents..."}
            </span>
          </div>
        ) : contracts.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-center gap-4">
            <div className="h-16 w-16 rounded-[2rem] bg-neutral-50 flex items-center justify-center border border-neutral-100 shadow-inner">
              <FileText className="h-8 w-8 text-neutral-200" />
            </div>
            <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest max-w-[200px] leading-relaxed">{t.empty}</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-neutral-50 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-neutral-50">
                  <th className="pl-6 py-4 uppercase text-[9px] font-bold tracking-widest text-neutral-400">{t.columns.title}</th>
                  <th className="py-4 uppercase text-[9px] font-bold tracking-widest text-neutral-400">{t.columns.status}</th>
                  <th className="py-4 uppercase text-[9px] font-bold tracking-widest text-neutral-400">{t.columns.date}</th>
                  <th className="pr-6 py-4 text-right uppercase text-[9px] font-bold tracking-widest text-neutral-400">{t.columns.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50">
                {contracts.map((contract) => (
                  <tr key={contract.id} className="group hover:bg-neutral-50/[0.3] transition-colors">
                    <td className="pl-6 py-5">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-bold text-neutral-800 group-hover:text-primary transition-colors">{contract.title}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] text-neutral-300 font-bold uppercase tracking-widest">ID: {contract.id}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-5">{getStatusBadge(contract.status)}</td>
                    <td className="py-5">
                      <span className="text-[11px] font-bold text-neutral-400 tabular-nums uppercase">
                        {format(new Date(contract.created_at), "dd/MM/yyyy", { locale: lang === 'pt' ? ptBR : enUS })}
                      </span>
                    </td>
                    <td className="pr-6 py-5 text-right">
                      <div className="flex justify-end gap-2">
                        {contract.status === 'pending' && contract.signing_url && (
                          <Button 
                            variant="secondary" 
                            size="micro" 
                            className="h-8 px-4 text-[10px] font-bold uppercase tracking-widest gap-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white border-blue-100 rounded-xl transition-all active:scale-95 shadow-sm shadow-blue-50"
                            onClick={() => window.open(contract.signing_url!, '_blank')}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {lang === 'pt' ? 'Assinar' : 'Sign'}
                          </Button>
                        )}
                        {contract.file_url && (
                          <Button 
                            variant="ghost" 
                            size="micro" 
                            className="h-8 w-8 p-0 rounded-xl hover:bg-neutral-100 text-neutral-400 hover:text-neutral-900 transition-colors"
                            onClick={() => window.open(contract.file_url!, '_blank')}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
